// Support-csapat create/edit — jobb oldali beúszó FormSheet, KÉT módban (a billing/employee-minta: külön
// create/edit instance). Kétnyelvű név (kötelező) + kétnyelvű leírás + kapcsolattartó e-mail + tagok
// (belső identityk, több-választós). A validálás forrás-igazsága a @atlas/contracts SupportTeamWriteBodySchema
// (a resolver a features/support-teams/form.ts segédein át). Create → POST; edit → PATCH If-Match/OCC + tag-diff.
import { type SupportTeamRowSchema } from '@atlas/contracts';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { BilingualField } from '@/components/shared/bilingual-field';
import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import { TextField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { supportTeamKeys } from '@/features/support-teams/api';
import {
  buildSupportTeamResolver,
  EMPTY_SUPPORT_TEAM_FORM,
  toSupportTeamBody,
  type SupportTeamFormValues,
} from '@/features/support-teams/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

type SupportTeamRow = z.infer<typeof SupportTeamRowSchema>;

export interface SupportTeamFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → LÉTREHOZÁS; egyébként a szerkesztendő csapat (OCC `version` + előtöltés a nyers i18n-mezőkből). */
  team: SupportTeamRow | null;
}

export function SupportTeamFormSheet({
  open,
  onOpenChange,
  team,
}: SupportTeamFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase().startsWith('en') ? ('en' as const) : ('hu' as const);
  const isEdit = team !== null;

  // Cross-domain kapu: a szolgáltatás-választó a BILLING (SHARED) katalógusba ír (más namespace, mint a
  // csapat/tagok workforce-a) — a `services.manage` feature-jog vezérli (a service-catalog write ugyanez).
  // A support-team BFF maga CSAK `support_teams.manage`-t vár, így a UI itt az ŐSZINTE tükör: jog híján a mező
  // disabled + generikus hint, és a beküldött body-ban a szolgáltatás-halmaz az EREDETI (nincs diff).
  const { can } = usePermissions();
  const canManageServices = can('services.manage');
  const originalServiceIds = useMemo(() => (team?.services ?? []).map((s) => s.id), [team]);

  // Felvehető tagok (belső identityk) — locale-tudatos név (a lang a cache-kulcs része).
  const refsQ = api.supportTeamRefs.useQuery(['support-team-refs', lang], { query: { lang } });
  const memberOptions =
    refsQ.data?.status === 200
      ? refsQ.data.body.members.map((m) => ({ id: m.id, name: m.name }))
      : [];
  const serviceOptions =
    refsQ.data?.status === 200
      ? (refsQ.data.body.services ?? []).map((s) => ({ id: s.id, name: s.name }))
      : [];

  const form = useForm<SupportTeamFormValues>({
    resolver: buildSupportTeamResolver(t),
    mode: 'onTouched',
    defaultValues: EMPTY_SUPPORT_TEAM_FORM,
  });
  const { reset, handleSubmit, setError, watch, setValue } = form;

  useEffect(() => {
    if (team) {
      reset({
        nameHu: team.nameHu,
        nameEn: team.nameEn,
        descriptionHu: team.descriptionHu ?? '',
        descriptionEn: team.descriptionEn ?? '',
        email: team.email ?? '',
        memberIds: team.members.map((m) => m.id),
        serviceIds: (team.services ?? []).map((s) => s.id),
      });
    } else {
      reset(EMPTY_SUPPORT_TEAM_FORM);
    }
  }, [team, reset]);

  const mutation = useEntityMutation<SupportTeamFormValues>({
    mutationFn: (values) =>
      isEdit
        ? api.supportTeamUpdate.mutation({
            params: { id: team.id },
            body: toSupportTeamBody(values),
            headers: { 'if-match': String(team.version) },
          })
        : api.supportTeamCreate.mutation({ body: toSupportTeamBody(values) }),
    invalidateKeys: [supportTeamKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_SUPPORT_TEAM_FORM) {
        setError(field as keyof SupportTeamFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:supportTeams.updated') : t('toast:supportTeams.created'),
      conflict: t('toast:supportTeams.conflict'),
      forbidden: t('toast:supportTeams.forbidden'),
      validation: t('toast:supportTeams.validation'),
      error: isEdit ? t('toast:supportTeams.error') : t('toast:supportTeams.createError'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    // Jog híján a szolgáltatás-halmaz az EREDETIRE kényszerítve (a mező amúgy is disabled) → a BFF-diff üres,
    // a body nem hordoz billing-írást. Joggal a form-állapot megy.
    const effective = canManageServices ? values : { ...values, serviceIds: originalServiceIds };
    mutation.mutate(effective, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_SUPPORT_TEAM_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('supportTeams.editTitle') : t('supportTeams.createTitle')}
      description={isEdit ? (team.name ?? undefined) : t('supportTeams.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('supportTeams.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('supportTeams.saving') : t('supportTeams.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <BilingualField<SupportTeamFormValues>
          variant="input"
          label={t('supportTeams.fields.name')}
          nameHu="nameHu"
          nameEn="nameEn"
          required
        />

        <TextField<SupportTeamFormValues>
          name="email"
          label={t('supportTeams.fields.email')}
          hint={t('supportTeams.fields.emailHint')}
          type="email"
          autoComplete="off"
          // A legacy `required|email` 1:1 — csapat elérhetőség nélkül a sor félkész (mind a 10 migrált csapatnak van).
          required
        />

        <BilingualField<SupportTeamFormValues>
          variant="textarea"
          label={t('supportTeams.fields.description')}
          nameHu="descriptionHu"
          nameEn="descriptionEn"
          rows={2}
        />

        {/* Szolgáltatások (a Tagok ELŐTT) — kereshető multi-select, mint a Tagok; a kiválasztott katalógus-
            szolgáltatások supportTeamId-ját e csapatra állítja a BFF (legacy client_support 1:1, csak SERVICE-szint). */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t('supportTeams.fields.services')}</p>
          <EntityMultiSelect
            options={serviceOptions}
            value={watch('serviceIds')}
            onChange={(ids) => setValue('serviceIds', ids, { shouldDirty: true })}
            placeholder={t('supportTeams.fields.servicesPlaceholder')}
            loading={refsQ.isLoading}
            disabled={!canManageServices}
            countLabel={(n) => t('supportTeams.fields.servicesCount', { count: n })}
            aria-label={t('supportTeams.fields.services')}
          />
          {!canManageServices && (
            <p className="text-muted-foreground text-xs">
              {t('supportTeams.fields.servicesNoPermission')}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t('supportTeams.fields.members')}</p>
          <EntityMultiSelect
            options={memberOptions}
            value={watch('memberIds')}
            onChange={(ids) => setValue('memberIds', ids, { shouldDirty: true })}
            placeholder={t('supportTeams.fields.membersPlaceholder')}
            loading={refsQ.isLoading}
            countLabel={(n) => t('supportTeams.fields.membersCount', { count: n })}
            aria-label={t('supportTeams.fields.members')}
          />
        </div>
      </Form>
    </FormSheet>
  );
}
